import React, { useState, useEffect } from 'react';
import { volleyballApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const AdminPanel = () => {
  // Categories state
  const [categories, setCategories] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedChampionships, setSelectedChampionships] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [isUpdating, setIsUpdating] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoadingData(true);
    try {
      const data = await volleyballApi.getChampionships();
      const championships = data.championships || [];
      
      // Group championships by category
      const grouped = groupChampionshipsByCategory(championships);
      setCategories(grouped);
      
      // Start with all categories collapsed
      setExpandedCategories(new Set());
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoadingData(false);
    }
  };

  const groupChampionshipsByCategory = (championships) => {
    const categoriesDict = {};
    
    for (const champ of championships) {
      const series = champ.displaySeries || champ.series || 'Unknown';
      const gender = champ.gender || '';
      const categoryKey = gender ? `${series} ${gender}` : series;
      
      if (!categoriesDict[categoryKey]) {
        categoriesDict[categoryKey] = {
          name: categoryKey,
          championships: []
        };
      }
      categoriesDict[categoryKey].championships.push(champ);
    }
    
    // Convert to array and sort by first championship order
    const categoriesArray = Object.values(categoriesDict);
    categoriesArray.sort((a, b) => {
      const minOrderA = Math.min(...a.championships.map(c => c.order || c.displayOrder || 999));
      const minOrderB = Math.min(...b.championships.map(c => c.order || c.displayOrder || 999));
      return minOrderA - minOrderB;
    });
    
    return categoriesArray;
  };

  const toggleCategory = (categoryName) => {
    const newExpanded = new Set(expandedCategories);
    
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const handleToggleSelect = (championshipId) => {
    const newSelected = new Set(selectedChampionships);
    if (newSelected.has(championshipId)) {
      newSelected.delete(championshipId);
    } else {
      newSelected.add(championshipId);
    }
    setSelectedChampionships(newSelected);
  };

  const handleSelectAll = () => {
    const allChampionships = categories.flatMap(cat => cat.championships);
    if (selectedChampionships.size === allChampionships.length) {
      setSelectedChampionships(new Set());
    } else {
      setSelectedChampionships(new Set(allChampionships.map(c => c.id)));
    }
  };

  const handleUpdateSelected = async () => {
    if (selectedChampionships.size === 0) {
      toast.error('Seleziona almeno un campionato');
      return;
    }

    setIsUpdating(true);
    try {
      await volleyballApi.updateChampionships(Array.from(selectedChampionships));
      toast.success(`Update completato per ${selectedChampionships.size} campionati`);
      setSelectedChampionships(new Set());
      // Reload data to show updated status
      await loadData();
    } catch (error) {
      console.error('Error updating championships:', error);
      toast.error('Errore nell\'update dei campionati');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateAll = async () => {
    setIsUpdating(true);
    try {
      await volleyballApi.updateAllChampionships();
      toast.success('Update completato per tutti i campionati');
      // Reload data to show updated status
      await loadData();
    } catch (error) {
      console.error('Error updating all championships:', error);
      toast.error('Errore nell\'update di tutti i campionati');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateSingle = async (championshipId) => {
    setIsUpdating(true);
    try {
      await volleyballApi.updateChampionships([championshipId]);
      toast.success('Update completato');
      // Reload data to show updated status
      await loadData();
    } catch (error) {
      console.error('Error updating championship:', error);
      toast.error('Errore nell\'update del campionato');
    } finally {
      setIsUpdating(false);
    }
  };

  const totalChampionships = categories.reduce((sum, cat) => sum + cat.championships.length, 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              Pannello Admin
            </h1>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">Azioni Globali</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={handleSelectAll}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                {selectedChampionships.size === totalChampionships 
                  ? 'Deseleziona Tutti' 
                  : 'Seleziona Tutti'}
              </Button>
              <Button
                onClick={handleUpdateSelected}
                disabled={selectedChampionships.size === 0 || isUpdating}
                size="sm"
                className="w-full sm:w-auto"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Update Selezionati ({selectedChampionships.size})
                  </>
                )}
              </Button>
              <Button
                onClick={handleUpdateAll}
                disabled={isUpdating}
                variant="destructive"
                size="sm"
                className="w-full sm:w-auto"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Update Tutti
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Categories List */}
        <Card className="mt-4 sm:mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg flex items-center justify-between">
              <span>
                Categorie ({categories.length}) - Campionati ({totalChampionships})
              </span>
              {isLoadingData && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-6">
            <div className="space-y-2 sm:space-y-3">
              {categories.map((category) => (
                <div
                  key={category.name}
                  className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm"
                >
                  {/* Category Header */}
                  <div 
                    className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-750 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    onClick={() => toggleCategory(category.name)}
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                        {category.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {category.championships.length} campionati
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {expandedCategories.has(category.name) ? (
                        <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-300" />
                      )}
                    </div>
                  </div>

                  {/* Championships in Category */}
                  {expandedCategories.has(category.name) && (
                    <div className="p-2 sm:p-4 space-y-2 animate-in slide-in-from-top-2 duration-300">
                      {category.championships.map((championship) => (
                        <div
                          key={championship.id}
                          className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                          {/* Checkbox */}
                          <Checkbox
                            checked={selectedChampionships.has(championship.id)}
                            onCheckedChange={() => handleToggleSelect(championship.id)}
                            className="flex-shrink-0"
                          />

                          {/* Championship Info */}
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {championship.displayName || championship.name}
                              </div>
                              {championship.hasData ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  ✓ Dati presenti
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                  ⚠ Nessun dato
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {championship.displaySeries || championship.series} • 
                              {championship.displayGroup || championship.group} • 
                              {championship.gender}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              ID: {championship.id}
                              {championship.hasData && (
                                <span className="ml-2">
                                  • {championship.matchCount || 0} partite • {championship.standingsCount || 0} squadre
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Single Update Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateSingle(championship.id)}
                            disabled={isUpdating}
                            className="flex-shrink-0 w-full sm:w-auto"
                          >
                            <RefreshCw className="h-4 w-4 sm:mr-1" />
                            <span className="sm:inline">Update</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;
